import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  X, // Header/Mobile Menu
  BookOpen, Book, Award, Check, // Grade Levels & Curricula
  Zap, Layers, Users, Edit, HelpCircle, Sun, // Specialized Programs
} from 'lucide-react';
import { asset } from '../../../shared/utils/asset';
import { SEO } from '../../../shared/components';

// Mock asset function: Since we are in a single file, we cannot use '../../../shared/utils/asset'. 
// We will use a function that returns the path directly, assuming the assets folder is accessible.

// --- Reusable Component for Subject/Curriculum Cards (To minimize repetition) ---
interface CurriculumCardProps {
    title: string;
    icon: React.ReactNode;
    children: React.ReactNode;
    className?: string;
}

const CurriculumCard: React.FC<CurriculumCardProps> = ({ title, icon, children, className = "" }) => (
    <div className={`bg-white rounded-xl shadow-lg text-foreground p-6 md:p-8 transition-all duration-300 border border-transparent animate-slide-up
                    hover:border-gray-100 hover:shadow-2xl ${className}`}>
        <div className="w-14 h-14 bg-cyan-100 rounded-full flex items-center justify-center mb-6">
            {icon}
        </div>
        <h3 className="text-xl font-display font-bold text-foreground mb-4">{title}</h3>
        <ul className="space-y-3 text-muted-foreground text-sm">
            {children}
        </ul>
    </div>
);

// --- Reusable Component for Features (Specialized Programs) ---
interface FeatureProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    className?: string;
}

const Feature: React.FC<FeatureProps> = ({ icon, title, description, className = "" }) => (
    <div className={`flex gap-6 animate-slide-up ${className}`}>
        <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
            {icon}
        </div>
        <div>
            <h3 className="text-xl font-display font-bold mb-3 text-white">{title}</h3>
            <p className="text-white/90">{description}</p>
        </div>
    </div>
);


const TuitionPointServices: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
        document.body.classList.toggle('overflow-hidden', !isMenuOpen);
    };

    // Helper for List Items
    const ListItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
        <li className="flex items-center">
            <Check className="w-4 h-4 text-accent mr-2 flex-shrink-0" />
            <span>{children}</span>
        </li>
    );

    return (
        <div className="bg-accent text-white min-h-screen">
            <SEO
                title="Tutoring Services Qatar - CBSE, IGCSE, IB, Federal Board | Tuition Point"
                description="Comprehensive tutoring services in Qatar: subject tutoring, entrance exam prep, crash courses, NIOS support, and summer programs. Expert guidance for CBSE, IGCSE, IB students in Al Wakra."
                keywords="subject tutoring Qatar, entrance exam preparation Qatar, crash courses Al Wakra, NIOS support Qatar, summer programs Qatar, mathematics tutoring, science coaching Qatar, board exam preparation, homework help Qatar"
            />

            {/* --------------------- Header --------------------- */}
            {/* NOTE: In a final React app, this Header would be a separate, persistent component (e.g., in App.tsx or Layout.tsx) */}
          
            
            {/* Mobile Menu Overlay */}
            <div className={`fixed inset-0 z-50 bg-white transition-transform duration-300 ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                 <div className="container mx-auto px-4 h-full flex flex-col py-8 max-w-7xl">
                    <div className="flex justify-between items-center mb-12">
                        <Link to="/" className="flex items-center space-x-2">
                            <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-white">
                                <span className="font-display font-bold text-lg">TP</span>
                            </div>
                            <span className="font-display font-bold text-primary text-lg">Tuition Point</span>
                        </Link>
                        <button onClick={toggleMenu} aria-label="Close Menu">
                            <X className="w-6 h-6 text-primary" />
                        </button>
                    </div>
                    <nav className="flex flex-col space-y-6 text-xl">
                        <Link to="/" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Home</Link>
                        <Link to="/about" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">About Us</Link>
                        <Link to="/services" onClick={toggleMenu} className="nav-link text-accent font-semibold">Services</Link>
                        <Link to="/team" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Our Team</Link>
                        <Link to="/achievements" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Success Stories</Link>
                        <Link to="/contact" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Contact</Link>
                        <Link to="/enrollment" onClick={toggleMenu} className="btn-primary w-full text-center mt-4">Enroll Now</Link>
                    </nav>
                </div>
            </div>
            
            {/* --------------------- Page Header --------------------- */}
            <section className="pt-16 pb-12 md:pt-20 md:pb-16 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto">
                        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 animate-slide-up">Tutoring Services Qatar - CBSE, IGCSE, IB & More</h1>
                        <p className="text-lg text-white animate-slide-up">Comprehensive tutoring services in Al Wakra for K-12 students. Expert coaching for CBSE, IGCSE, IB, Federal & Bangladesh Board with entrance exam preparation.</p>
                    </div>
                </div>
            </section>
            
            {/* --------------------- Grade Levels and Subjects --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4">K-12 Tutoring by Grade Level & Subject</h2>
                        <p className="text-lg text-white/80">Expert subject tutoring in Al Wakra for all grade levels from kindergarten to grade 12. Mathematics, Science, English, and more.</p>
                    </div>
                    
                    {/* CBSE Section */}
                    <div className="mb-16">
                        <h3 className="text-3xl font-bold mb-8 px-4 border-l-4 rounded text-primary" style={{borderColor: '#011c63'}}>CBSE Tutoring Qatar</h3>
        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            <CurriculumCard title="Elementary Grades (K.G - 5)" icon={<BookOpen className="w-6 h-6 text-accent" />} className="animate-slide-up">
                                <ListItem>Mathematics</ListItem>
                                <ListItem>English Language Arts</ListItem>
                                <ListItem>Science</ListItem>
                                <ListItem>Social Studies</ListItem>
                                <ListItem>Hindi</ListItem>
                            </CurriculumCard>
                            
                            <CurriculumCard title="Middle School (Grades 6-8)" icon={<Book className="w-6 h-6 text-accent" />} className="animate-slide-up">
                                <ListItem>Mathematics</ListItem>
                                <ListItem>English</ListItem>
                                <ListItem>General Science</ListItem>
                                <ListItem>Social Studies</ListItem>
                                <ListItem>Hindi</ListItem>
                            </CurriculumCard>
                            
                            <CurriculumCard title="High School (Grades 9-12)" icon={<Award className="w-6 h-6 text-accent" />} className="animate-slide-up">
                                <ListItem>Mathematics</ListItem>
                                <ListItem>Physics</ListItem>
                                <ListItem>Chemistry</ListItem>
                                <ListItem>Biology</ListItem>
                                <ListItem>Business Studies</ListItem>
                                <ListItem>Economics</ListItem>
                                <ListItem>Accountancy</ListItem>
                            </CurriculumCard>
                        </div>
                    </div>
        
                    {/* IGCSE Section */}
                    <div className="mb-16">
                        <h2 className="text-3xl font-bold mb-8 px-4 border-l-4 rounded text-primary" style={{borderColor: '#011c63'}}>IGCSE / AS & A Level</h2>
        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            <CurriculumCard title="Cambridge Level" icon={<BookOpen className="w-6 h-6 text-accent" />} className="animate-slide-up">
                                <ListItem>O Level</ListItem>
                                <ListItem>As Level</ListItem>
                                <ListItem>A Level</ListItem>
                            </CurriculumCard>
                            
                            <CurriculumCard title="Edexcel Level" icon={<Book className="w-6 h-6 text-accent" />} className="animate-slide-up">
                                <ListItem>O Level</ListItem>
                                <ListItem>As Level</ListItem>
                                <ListItem>A Level</ListItem>
                            </CurriculumCard>
                        </div>
                    </div>
        
                    {/* IB Section */}
                    <div className="mb-16">
                        <h2 className="text-3xl font-bold mb-8 px-4 border-l-4 rounded text-primary" style={{borderColor: '#011c63'}}>IB (International Baccalaureate)</h2>
        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            <CurriculumCard title="ASCA Levels" icon={<BookOpen className="w-6 h-6 text-accent" />} className="animate-slide-up">
                                <ListItem>AP Calculus</ListItem>
                                <ListItem>SAT</ListItem>
                            </CurriculumCard>
                        </div>
                    </div>

                    {/* Competitive Exams Section */}
                    <div className="mb-0">
                        <h2 className="text-3xl font-bold mb-8 px-4 border-l-4 rounded text-primary" style={{borderColor: '#011c63'}}>Competitive Exams Preparation</h2>
        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            <CurriculumCard title="Specialized Exam Coaching" icon={<BookOpen className="w-6 h-6 text-accent" />} className="animate-slide-up">
                                <ListItem>IIT JEE (Mains and Advanced)</ListItem>
                                <ListItem>NEET</ListItem>
                                <ListItem>Olympiad Exams</ListItem>
                                <ListItem>SAT</ListItem>
                            </CurriculumCard>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* --------------------- Specialized Programs --------------------- */}
            <section className="py-24 bg-accent text-white">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4">Specialized Coaching Programs in Al Wakra</h2>
                        <p className="text-lg text-white">Beyond regular tutoring, we offer specialized programs including entrance exam preparation (IIT-JEE, NEET), crash courses, summer programs, and NIOS support.</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-12 gap-x-8">
                        <Feature
                            icon={<Zap className="w-6 h-6 text-accent" />}
                            title="Crash Courses in Al Wakra"
                            description="Intensive crash courses in Qatar designed to help students quickly master specific subjects before board exams. Targeted instruction on key CBSE, IGCSE, and IB concepts with proven exam strategies."
                            className="animate-slide-up animation-delay-1"
                        />

                        <Feature
                            icon={<Layers className="w-6 h-6 text-accent" />}
                            title="Foundation Courses"
                            description="Build a strong academic foundation with our comprehensive programs designed to establish thorough understanding of fundamental concepts that will support future learning."
                            className="animate-slide-up animation-delay-2"
                        />

                        <Feature
                            icon={<Users className="w-6 h-6 text-accent" />}
                            title="Entrance Exam Preparation Qatar"
                            description="Specialized coaching for IIT-JEE, NEET, SAT, and school entrance examinations in Al Wakra. Expert tutors provide mock tests, personalized feedback, and proven strategies for BPS, DPS, and college admissions."
                            className="animate-slide-up animation-delay-3"
                        />

                        <Feature
                            icon={<Edit className="w-6 h-6 text-accent" />}
                            title="NIOS Open Schooling Support Qatar"
                            description="Comprehensive NIOS tutoring support in Al Wakra for students pursuing National Institute of Open Schooling. Expert coaching in Science, Mathematics, and all NIOS subjects with exam preparation."
                            className="animate-slide-up animation-delay-4"
                        />

                        <Feature
                            icon={<HelpCircle className="w-6 h-6 text-accent" />}
                            title="Homework Help"
                            description="One-on-one assistance with daily assignments to ensure students understand concepts and develop good study habits. We guide rather than simply provide answers."
                            className="animate-slide-up animation-delay-5"
                        />

                        <Feature
                            icon={<Sun className="w-6 h-6 text-accent" />}
                            title="Summer Courses"
                            description="Engaging and enriching programs during the summer break that combine academic learning with fun activities to keep students intellectually stimulated."
                            className="animate-slide-up animation-delay-6"
                        />
                    </div>
                </div>
            </section>
            
            {/* --------------------- Supported Curricula --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4">Supported Curricula</h2>
                        <p className="text-lg text-white/80">We provide tutoring for students from various educational boards and systems.</p>
                    </div>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8">
                        {/* Note: Using the 'card' class for simple shadow/background */}
                        <div className="bg-white rounded-xl shadow-lg text-foreground text-center py-8 px-4 animate-slide-up animation-delay-1">
                            <div className="text-4xl font-display font-bold text-accent mb-2">CBSE</div>
                            <p className="text-muted-foreground text-sm">Central Board of Secondary Education</p>
                        </div>
                        
                        <div className="bg-white rounded-xl shadow-lg text-foreground text-center py-8 px-4 animate-slide-up animation-delay-2">
                            <div className="text-4xl font-display font-bold text-accent mb-2">IGCSE</div>
                            <p className="text-muted-foreground text-sm">International General Certificate of Secondary Education</p>
                        </div>
                        
                        <div className="bg-white rounded-xl shadow-lg text-foreground text-center py-8 px-4 animate-slide-up animation-delay-3">
                            <div className="text-4xl font-display font-bold text-accent mb-2">IB</div>
                            <p className="text-muted-foreground text-sm">International Baccalaureate</p>
                        </div>
                        
                        <div className="bg-white rounded-xl shadow-lg text-foreground text-center py-8 px-4 animate-slide-up animation-delay-4">
                            <div className="text-4xl font-display font-bold text-accent mb-2">Federal</div>
                            <p className="text-muted-foreground text-sm">Federal Board Curriculum</p>
                        </div>
                        
                        <div className="bg-white rounded-xl shadow-lg text-foreground text-center py-8 px-4 animate-slide-up animation-delay-5">
                            <div className="text-4xl font-display font-bold text-accent mb-2">BD</div>
                            <p className="text-muted-foreground text-sm">Bangladesh Board</p>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* --------------------- Teaching Methodology --------------------- */}
            <section className="py-24 bg-accent text-white">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4">Our Teaching Methodology</h2>
                        <p className="text-lg text-white/90">We employ proven teaching methods that adapt to each student's learning style.</p>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="relative animate-slide-up">
                            {/* Accent background blur */}
                            <div className="absolute -top-10 -left-10 w-64 h-64   rounded-full filter blur-3xl z-0"></div>
                           <img
                                                          src={asset('new_about.jpeg')}
                                                          alt="Students studying at Tuition Point"
                                                          className="w-full h-auto rounded-2xl shadow-xl relative z-10"
                                                      />
                        </div>
                        
                        <div className="space-y-8 animate-slide-up">
                            <div className="bg-white rounded-xl shadow-lg text-foreground p-6">
                                <h3 className="text-xl font-display font-bold mb-3">Personalized Learning Plans</h3>
                                <p className="text-muted-foreground">
                                    We develop individualized learning plans for each student based on their academic needs, learning style, and goals. This ensures that our instruction is relevant and effective for each learner.
                                </p>
                            </div>

                            <div className="bg-white rounded-xl shadow-lg text-foreground p-6">
                                <h3 className="text-xl font-display font-bold mb-3">Interactive Approach</h3>
                                <p className="text-muted-foreground">
                                    We believe in active learning through discussions, problem-solving, and real-world applications. Our teaching goes beyond theoretical knowledge to help students understand practical relevance.
                                </p>
                            </div>

                            <div className="bg-white rounded-xl shadow-lg text-foreground p-6">
                                <h3 className="text-xl font-display font-bold mb-3">Regular Assessment</h3>
                                <p className="text-muted-foreground">
                                    We conduct regular formative and summative assessments to track progress and identify areas for improvement. This data-driven approach allows us to adjust our teaching methods as needed.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* --------------------- Pricing --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4">Flexible Pricing Options</h2>
                        <p className="text-lg text-white/80">We offer various tuition packages to suit different needs and schedules.</p>
                    </div>
                    
                    <div className="flex flex-col items-center">
                        <div className="bg-white rounded-xl shadow-lg text-foreground max-w-4xl w-full p-8 lg:p-12 animate-slide-up shadow-xl">
                            <div className="text-center mb-8">
                                <p className="text-lg text-muted-foreground">Our pricing varies based on grade level, subject, and program type. We strive to provide competitive rates while maintaining the highest quality of education.</p>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-xl font-display font-bold mb-4">Factors Affecting Pricing:</h3>
                                    <ul className="space-y-3 text-muted-foreground">
                                        <ListItem>Grade level and subject complexity</ListItem>
                                        <ListItem>Individual vs. group tuition</ListItem>
                                        <ListItem>Frequency and duration of sessions</ListItem>
                                        <ListItem>Specialized programs vs. regular tutoring</ListItem>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-xl font-display font-bold mb-4">Payment Options:</h3>
                                    <ul className="space-y-3 text-muted-foreground">
                                        <ListItem>Monthly payment plans</ListItem>
                                        <ListItem>Term-based packages (discounted)</ListItem>
                                        <ListItem>Subject bundle discounts</ListItem>
                                        <ListItem>Sibling discounts available</ListItem>
                                    </ul>
                                </div>
                            </div>
                            
                            <div className="text-center mt-12">
                                <p className="text-accent font-semibold text-lg mb-4">For detailed pricing information specific to your needs, please contact us.</p>
                                <Link to="/contact" className="btn-primary" style={{backgroundColor: '#011c63'}}>
                                    Request Pricing Details
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* --------------------- CTA --------------------- */}
            <section className="py-24 bg-accent text-white">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-3xl md:text-4xl font-display font-semibold mb-6 animate-slide-up">Ready to Begin Your Educational Journey?</h2>
                        <p className="text-lg mb-10 text-white text-opacity-90 animate-slide-up">Take the first step toward academic excellence with Tuition Point Education Center.</p>
                       <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
                                     {/* Buttons kept with explicit styling for high contrast against the bg-accent section */}
                                     <Link 
                                       to="/enrollment" 
                                       className="bg-white text-accent hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg shadow-xl transition duration-300 sm:w-auto text-lg"
                                     >
                                       Enroll Today
                                     </Link>
                                     <Link 
                                       to="/contact" 
                                       className="border-2 border-white text-white hover:bg-white hover:text-accent font-semibold py-3 px-8 rounded-lg transition duration-300 sm:w-auto text-lg"
                                     >
                                       Contact Us
                                     </Link>
                                   </div>
                    </div>
                </div>
            </section>
            
           
        </div>
    );
};

export default TuitionPointServices;
